<script setup>
import { computed, reactive, ref, watch } from 'vue'
import ModalDialog from './ModalDialog.vue'
import { centsToMoney, formatCurrency, saleTotalCents, serializeSale, validateSale } from '../api/commerce'

const props = defineProps({
  open: Boolean, catalog: { type: Array, default: () => [] }, saving: Boolean, error: { type: String, default: '' }, bookingId: { type: Number, default: null },
})
const emit = defineEmits(['close', 'save'])
const form = reactive({ lines: [], notes: '' })
const validationError = ref('')
const availableCatalog = computed(() => props.catalog.filter((item) => item.is_active !== false))
const total = computed(() => centsToMoney(saleTotalCents(form.lines)))
let nextLineId = 0

function addLine() {
  const item = availableCatalog.value.find((entry) => !form.lines.some((line) => String(line.item_id) === String(entry.id)))
  form.lines.push({ key: ++nextLineId, item_id: item?.id || '', quantity: 1, unit_price: item?.unit_price ?? '' })
}

function itemFor(line) { return props.catalog.find((item) => String(item.id) === String(line.item_id)) }
function selectItem(line) { line.unit_price = itemFor(line)?.unit_price ?? '' }

watch(() => props.open, (open) => {
  if (!open) return
  validationError.value = ''
  form.lines = []
  form.notes = ''
  addLine()
})

function submit() {
  if (props.saving) return
  validationError.value = validateSale(form, props.catalog)
  if (!validationError.value) emit('save', serializeSale(form, props.bookingId))
}
</script>

<template>
  <ModalDialog :open="open" :title="bookingId ? `Продажа к брони №${bookingId}` : 'Новая продажа'" eyebrow="Товары и услуги" wide @close="!saving && emit('close')">
    <form class="dialog-form" @submit.prevent="submit">
      <p class="sale-help">После проведения продажа не редактируется и не удаляется. При ошибке отмените её с указанием причины и создайте новую.</p>
      <fieldset class="sale-fields" :disabled="saving">
        <div v-for="(line, index) in form.lines" :key="line.key" class="sale-line-editor">
          <label class="field sale-line-item"><span>Позиция {{ index + 1 }}</span>
            <select v-model="line.item_id" required @change="selectItem(line)">
              <option disabled value="">Выберите товар или услугу</option>
              <option v-for="item in availableCatalog" :key="item.id" :value="item.id">{{ item.name }} · {{ item.kind === 'product' ? `остаток ${item.stock_quantity} ${item.unit}` : 'услуга' }}</option>
            </select>
          </label>
          <label class="field"><span>Количество</span><input v-model="line.quantity" type="number" min="1" max="2147483647" step="1" required /></label>
          <label class="field"><span>Цена за единицу, ₽</span><input v-model="line.unit_price" type="text" inputmode="decimal" required /></label>
          <button class="button button--ghost sale-line-remove" type="button" :aria-label="`Убрать несохранённую позицию ${index + 1}`" @click="form.lines.splice(index, 1)">Убрать</button>
        </div>
        <button class="button button--secondary button--small" type="button" :disabled="form.lines.length >= 100 || !availableCatalog.length" @click="addLine">Добавить позицию</button>
        <label class="field sale-notes"><span>Комментарий / причина изменения цены</span><textarea v-model="form.notes" rows="2" maxlength="2000" placeholder="Обязательно, если цена отличается от справочника" /></label>
      </fieldset>
      <p class="sale-total">Итого <strong>{{ formatCurrency(total) }}</strong></p>
      <p v-if="!availableCatalog.length" class="form-error" role="alert">В справочнике нет активных товаров и услуг. Их добавляет администратор.</p>
      <p v-if="validationError || error" class="form-error" role="alert">{{ validationError || error }}</p>
      <footer class="modal-actions">
        <button class="button button--ghost" type="button" :disabled="saving" @click="emit('close')">Отмена</button>
        <button class="button button--primary" type="submit" :disabled="saving || !availableCatalog.length">{{ saving ? 'Проводим…' : 'Провести продажу' }}</button>
      </footer>
    </form>
  </ModalDialog>
</template>

<style scoped>
.sale-help { margin: 0 0 20px; color: var(--muted); font-size: .78rem; line-height: 1.5; }
.sale-fields { padding: 0; border: 0; margin: 0; min-width: 0; }
.sale-line-editor { display: grid; grid-template-columns: minmax(170px, 2fr) minmax(80px, .65fr) minmax(110px, 1fr) auto; gap: 12px; align-items: start; }
.sale-line-remove { margin-top: 25px; padding-inline: 8px; }
.sale-notes { margin-top: 20px; }
.sale-total { display: flex; justify-content: space-between; gap: 15px; margin: 18px 0; font-size: 1rem; }
.sale-total strong { color: var(--forest); }
@media (max-width: 650px) { .sale-line-editor { grid-template-columns: 1fr 1fr; padding-bottom: 12px; margin-bottom: 12px; border-bottom: 1px solid var(--line); } .sale-line-item { grid-column: 1 / -1; } .sale-line-remove { margin-top: 0; justify-self: start; } }
</style>
